/* istanbul ignore file */
import { schema } from 'normalizr';

const availableVasSchema = new schema.Entity(
  'availableVas',
  {},
  {
    idAttribute: value => value.type || value.vasType,
    processStrategy: value => ({
      ...value,
      type: value.type || value.vasType,
    }),
  },
);

const valueAddedService = new schema.Entity(
  'vas',
  {
    valueAddedService: availableVasSchema,
  },
  {
    processStrategy: value => ({
      ...value,
      type: value.type || value.vasType,
    }),
  },
);

const promotionCodeSchema = new schema.Entity(
  'promoCodes',
  {},
  { idAttribute: val => val.code || val.id },
);
const productContentSchema = new schema.Entity(
  'productContent',
  {},
  {
    idAttribute: (val, parent, key) => {
      switch (key) {
        case 'content':
          return parent.id;
        case 'productContent':
          return parent.merchProduct;

        default:
          return parent.id;
      }
    },
  },
);
const productImageSchema = new schema.Entity(
  'productImages',
  {},
  {
    idAttribute: (val, parent, key) => {
      switch (key) {
        case 'imageSet':
          return parent.id;

        case 'imageUrls':
          return parent.merchProduct;
        default:
          return parent.id;
      }
    },
  },
);

const product = new schema.Entity(
  'products',
  {
    content: productContentSchema,
    valueAddedServices: [valueAddedService],
    imageSet: productImageSchema,
    availableVas: [availableVasSchema],
  },
  {
    processStrategy: value => ({
      ...value,
      // double the valueAddedSerivces here so we can normalize by type and id
      availableVas: value.valueAddedServices,
      vas: value.valueAddedServices,
      isNikeId: value?.styleType === 'NIKEID', // needed?
      isGiftCard: value?.styleType === 'GIFT_CARD', // needed?
      hasWarranty: Boolean(value?.valueAddedServices?.find(item => item?.type === 'WARRANTY')),
    }),
  },
);

const warning = new schema.Entity(
  'warnings',
  {},
  {
    idAttribute: value => value.item?.id,
  },
);

const sku = new schema.Entity(
  'skus',
  {
    productId: product,
    product,
    valueAddedServices: [valueAddedService],
  },
  {
    processStrategy: (value, parent) => {
      const shape = {
        ...value,
        productId: value.productId || value.product?.id || parent?.id,
        // the relationship is actually via product so we can define that here and include it in the schema entity map above
        valueAddedServices: value?.product?.valueAddedServices || parent?.valueAddedServices || [],
      };
      return shape;
    },
  },
);

const listItem = new schema.Entity('listItems', {
  productId: product,
  skuId: sku,
});

const listSchema = new schema.Entity(
  'list',
  {
    items: [listItem],
  },
  { idAttribute: 'country' },
);

product.define({ skus: [sku] });
valueAddedService.define({
  imageSet: productImageSchema,
});

const fulfillmentOfferingItem = new schema.Entity(
  'offeringItems',
  {},
  {
    // we will normalize by skuId AND item id to allow looking up by both
    idAttribute: (value, parent, key) => {
      return key === 'skuOfferings' ? value.skuId : value.id;
    },
    // to minimize the amount of ids stored we'll take only the fulfillmentOfferings from value
    // alternately we can work with just skuId but we don't know what happens with cart with multiple skuIds that are same
    processStrategy: val => ({ fulfillmentOfferings: val?.fulfillmentOfferings }),
  },
);

const resultSchema = new schema.Entity(
  'locationSearchResults',
  {},
  {
    processStrategy: value => {
      const { description, ...details } = value?.details || {};
      return {
        ...value,
        // NOTE we could flatten it? or leave it?
        // name: details?.name,
        // postalAddress: details?.postalAddress,
        // searchProximity: details?.searchProximity,
        // NOTE but in every case we should not preserve details.description field as it contains unbounded and unused html we'd be storing in state
        details,
      };
    },
  },
);

const locationsSchema = new schema.Entity(
  'locations',
  {
    results: [resultSchema],
  },
  {
    // most locations have top level ids, but search locations do not - ids are in nested results
    idAttribute: value => value.id || 'SEARCH',
  },
);

const itemCostSchema = new schema.Entity(
  'itemCosts',
  {
    // via foffs
    promotionDiscounts: [promotionCodeSchema],
  },
  {
    idAttribute: (val, parent) => parent.id,
  },
);
const cartItem = new schema.Entity(
  'cartItems',
  {
    skuId: sku,
    // NOTE this comment is for review only and will be modified
    // we do not need to create this relationship unless we also intend to normalize instructionIds which we don't need to
    // valueAddedServices: [valueAddedService],
    promotionDiscounts: [promotionCodeSchema], // via shipping options
    itemCosts: itemCostSchema, // has promotionDiscounts via foffs
  },
  {
    processStrategy: value => ({
      ...value,
      productId: value.productId || value?.sku?.productId,
    }),
  },
);

const fulfillmentGroupSchema = new schema.Entity(
  'fulfillmentGroup',
  {
    items: [cartItem],
  },
  {
    idAttribute: val => val.fulfillmentDetails?.type,
  },
);

const cartTotals = new schema.Entity(
  'cartTotals',
  {},
  {
    idAttribute: (val, parent) => (val.country ? val.country : parent.country),
    processStrategy: (val, parent) => {
      if (parent.fulfillmentGroups) {
        return {
          summaryTotal: val.total,
          summarySubtotal: val.items.details?.price,
          summaryDiscountTotal: val.items.details?.discount,
          summaryEstimatedTax: val.taxes?.total,
          summaryEstimatedShipping: val.fulfillment?.total,
          summaryVasTotal: val.valueAddedServices?.total,
          ...val,
        };
      }
      return {
        summaryTotal: val.total,
        summarySubtotal: val.subtotal,
        summaryDiscountTotal: val.discountTotal,
        summaryEstimatedTax: val.taxTotal,
        summaryEstimatedShipping: val.shippingTotal,
        summaryVasTotal: val.valueAddedServicesTotal,
        ...val,
      };
    },
  },
);

const partnerCartReviews = new schema.Entity(
  'partnerCartReviews',
  {
    totals: cartTotals,
    items: [cartItem],
  },
  {
    idAttribute: 'country',
  },
);

const fulfillmentOfferingsSchema = new schema.Entity(
  'fulfillmentOfferings',
  {
    cartReviews: {
      response: {
        fulfillmentGroups: [fulfillmentGroupSchema],
        totals: cartTotals, // from cartReviews v2
      },
    },
    items: [fulfillmentOfferingItem],
    locations: [locationsSchema],
    skuOfferings: [fulfillmentOfferingItem],
    warnings: [warning],
    promotionCodes: [promotionCodeSchema],
  },
  {
    idAttribute: 'country',
    processStrategy: value => ({
      ...value,
      items: value.items,
      skuOfferings: value.items,
    }),
  },
);

const fulfillmentTypesSchema = new schema.Entity('fulfillmentTypes', {}, { idAttribute: 'type' });

const availableFulfillmentTypesSchema = new schema.Entity(
  'availableFulfillmentTypes',
  {
    fulfillmentOfferings: fulfillmentOfferingsSchema,
    fulfillmentTypes: [fulfillmentTypesSchema],
  },
  { idAttribute: 'country' },
);

const pickupOffering = new schema.Entity(
  'pickupOfferings',
  {},
  {
    idAttribute: value => value.location.id,
  },
);

const shipOffering = new schema.Entity(
  'shipOfferings',
  {},
  {
    idAttribute: (value, parent) => parent.skuId || parent.id,
    processStrategy: val => [val],
    mergeStrategy: (entityA, entityB) => [...entityA, ...entityB],
  },
);
const fulfillmentOfferingSchema = new schema.Union(
  {
    PICKUP: pickupOffering,
    SHIP: shipOffering,
  },
  value => value.type,
);

const persistedFulfillmentOfferingItem = new schema.Entity(
  'persistedOfferingItems',
  {
    fulfillmentOfferings: [fulfillmentOfferingSchema],
  },
  {
    idAttribute: ({ skuId }) => skuId,
  },
);

const persistedFulfillmentOfferingsSchema = new schema.Entity(
  'persistedFulfillmentOfferings',
  {
    cartReviews: {
      response: {
        fulfillmentGroups: [fulfillmentGroupSchema],
        totals: cartTotals, // from cartReviews v2
      },
    },
    items: [persistedFulfillmentOfferingItem],
    locations: [locationsSchema],
    skuOfferings: [persistedFulfillmentOfferingItem],
    warnings: [warning],
    promotionCodes: [promotionCodeSchema],
  },
  {
    idAttribute: 'country',
    processStrategy: value => ({
      ...value,
      items: value.items,
      skuOfferings: value.items,
    }),
  },
);

const persistedAvailableFulfillmentTypesSchema = new schema.Entity(
  'persistedAvailableFulfillmentTypes',
  {
    fulfillmentOfferings: persistedFulfillmentOfferingsSchema,
    fulfillmentTypes: [fulfillmentTypesSchema],
  },
  { idAttribute: 'country' },
);

export const cartSchema = new schema.Entity(
  'cart',
  {
    items: [cartItem],
    promotionCodesDetails: [promotionCodeSchema],
  },
  { idAttribute: 'country' }, // NOTE - we might want to do this off cart.id in the future, not necessary yet. Removing this option achieves that.
);

// NOTE we need availableVas by type and vas as is by id
export const dataSchema = {
  cart: cartSchema,
  cartItems: [cartItem],
  partnerCartReviews: partnerCartReviews,
  fulfillmentOfferings: fulfillmentOfferingsSchema,
  persistedFulfillmentOfferings: persistedFulfillmentOfferingsSchema,
  skus: [sku],
  products: [product],
  availableFulfillmentTypes: availableFulfillmentTypesSchema,
  persistedAvailableFulfillmentTypes: persistedAvailableFulfillmentTypesSchema,
  list: listSchema,
};
